/**
 * Custom Hook: usePesoCompliance
 * Manages state and logic for PESO compliance operations
 * Files are cached in-memory on server - no persistent storage
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-004
 */

import { useState, useCallback } from 'react';
import { inventoryBatchService } from '@/services/commerce/inventoryBatchService';
import type {
  BatchResponse,
  PesoComplianceRequest,
  PesoComplianceResponse
} from '@/types/inventory';

interface UsePesoComplianceOptions {
  onSuccess?: (response: PesoComplianceResponse) => void;
  onError?: (error: Error) => void;
}

interface UsePesoComplianceReturn {
  isMarking: boolean;
  isDeleting: boolean;
  isDownloading: boolean;
  isFetchingExpiring: boolean;
  error: string | null;
  markPesoCompliant: (
    batchId: number,
    request: PesoComplianceRequest,
    file?: File
  ) => Promise<PesoComplianceResponse | null>;
  downloadCertificate: (batchId: number, filename?: string) => Promise<void>;
  deletePesoCompliance: (batchId: number) => Promise<void>;
  getPesoExpiringBatches: (days?: number) => Promise<BatchResponse[]>;
  reset: () => void;
  validateCertificateFile: (file: File) => { valid: boolean; error?: string };
}

/**
 * Allowed file types for PESO certificate upload
 */
const ALLOWED_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

/**
 * Hook for managing PESO compliance operations
 */
export function usePesoCompliance(
  options: UsePesoComplianceOptions = {}
): UsePesoComplianceReturn {
  const [isMarking, setIsMarking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isFetchingExpiring, setIsFetchingExpiring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Validate certificate file
   */
  const validateCertificateFile = useCallback((file: File): { valid: boolean; error?: string } => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size must not exceed 5MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
      };
    }

    // Check file type
    const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const isValidType = ALLOWED_FILE_TYPES.includes(file.type);
    const isValidExtension = ALLOWED_FILE_EXTENSIONS.includes(fileExtension);

    if (!isValidType && !isValidExtension) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: PDF, JPEG, PNG. Received: ${file.type || fileExtension}`
      };
    }

    return { valid: true };
  }, []);

  /**
   * Mark batch as PESO compliant
   * File is stored in-memory cache on server (30 min TTL)
   */
  async function markPesoCompliant(
    batchId: number,
    request: PesoComplianceRequest,
    file?: File
  ): Promise<PesoComplianceResponse | null> {
    try {
      setIsMarking(true);
      setError(null);

      // Validate file if provided
      if (file) {
        const validation = validateCertificateFile(file);
        if (!validation.valid) {
          throw new Error(validation.error);
        }
      }

      const response = await inventoryBatchService.markPesoCompliant(
        batchId,
        request,
        file
      );

      if (options.onSuccess) {
        options.onSuccess(response);
      }

      return response;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to mark batch as PESO compliant';
      setError(errorMessage);

      if (options.onError) {
        options.onError(err instanceof Error ? err : new Error(errorMessage));
      }

      return null;
    } finally {
      setIsMarking(false);
    }
  }

  /**
   * Download PESO certificate file
   * File is retrieved from in-memory cache
   */
  async function downloadCertificate(batchId: number, filename?: string): Promise<void> {
    try {
      setIsDownloading(true);
      setError(null);

      await inventoryBatchService.downloadPesoCertificate(batchId, filename);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to download PESO certificate';
      setError(errorMessage);

      if (options.onError) {
        options.onError(err instanceof Error ? err : new Error(errorMessage));
      }
    } finally {
      setIsDownloading(false);
    }
  }

  /**
   * Delete PESO compliance marking and certificate
   * Removes from database and clears memory cache
   */
  async function deletePesoCompliance(batchId: number): Promise<void> {
    try {
      setIsDeleting(true);
      setError(null);

      await inventoryBatchService.deletePesoCompliance(batchId);

      if (options.onSuccess) {
        options.onSuccess({
          batchId,
          batchNumber: '',
          certificateNumber: '',
          issueDate: '',
          expiryDate: '',
          inspectorName: '',
          hasFile: false,
          downloadUrl: '',
          daysUntilExpiry: 0,
          markedBy: '',
          markedAt: ''
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to delete PESO compliance';
      setError(errorMessage);

      if (options.onError) {
        options.onError(err instanceof Error ? err : new Error(errorMessage));
      }
    } finally {
      setIsDeleting(false);
    }
  }

  /**
   * Get batches with expiring PESO certificates
   */
  async function getPesoExpiringBatches(days: number = 30): Promise<BatchResponse[]> {
    try {
      setIsFetchingExpiring(true);
      setError(null);

      const batches = await inventoryBatchService.getPesoExpiringBatches(days);
      return batches;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to fetch expiring PESO batches';
      setError(errorMessage);

      if (options.onError) {
        options.onError(err instanceof Error ? err : new Error(errorMessage));
      }

      return [];
    } finally {
      setIsFetchingExpiring(false);
    }
  }

  /**
   * Reset hook state
   */
  const reset = useCallback((): void => {
    setIsMarking(false);
    setIsDeleting(false);
    setIsDownloading(false);
    setIsFetchingExpiring(false);
    setError(null);
  }, []);

  return {
    isMarking,
    isDeleting,
    isDownloading,
    isFetchingExpiring,
    error,
    markPesoCompliant,
    downloadCertificate,
    deletePesoCompliance,
    getPesoExpiringBatches,
    reset,
    validateCertificateFile
  };
}
